import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { getSeriesStyle } from './chartTheme.js'
import './chart.css'

export interface ProgressChartRendererProps {
  spec: ChartSpec
  data: ChartResult
  className?: string
  testId?: string
}

interface ProgressItem {
  key: string
  label: string
  value: number
}

export function ProgressChartRenderer({
  spec,
  data,
  className = '',
  testId,
}: ProgressChartRendererProps) {
  // Determine items: either from series points (breakdown) or series list
  const items: ProgressItem[] = data.series.length === 1
    ? data.series[0].points.map((p, idx) => ({
        key: `p_${idx}`,
        label: String(p.x),
        value: p.y,
      }))
    : data.series.map((s, idx) => ({
        key: s.key || `s_${idx}`,
        label: s.label || `Series ${idx + 1}`,
        value: s.points.reduce((acc, p) => acc + p.y, 0),
      }))

  const target = typeof data.target === 'number'
    ? data.target
    : (spec.target?.kind === 'number' ? spec.target.value : null)

  const maxVal = target ?? Math.max(...items.map((i) => i.value), 1)

  return (
    <div
      className={`chartProgressList ${className}`}
      data-testid={testId}
      role="region"
      aria-label={spec.title || 'Progress Chart'}
    >
      {items.map((item, idx) => {
        const pct = maxVal > 0 ? Math.min(Math.max((item.value / maxVal) * 100, 0), 100) : 0
        const style = getSeriesStyle(idx, spec.style?.seriesColor?.[item.key])

        return (
          <div key={item.key} className="chartProgressRow" data-testid="chart-progress-row">
            <div className="chartProgressHeader">
              <span className="chartProgressLabel">{item.label}</span>
              <span className="chartProgressValue">
                {item.value.toLocaleString()}
                {target !== null && ` / ${target.toLocaleString()} (${Math.round(pct)}%)`}
              </span>
            </div>
            <div
              className="chartProgressTrack"
              role="progressbar"
              aria-valuenow={item.value}
              aria-valuemin={0}
              aria-valuemax={maxVal}
              aria-label={item.label}
            >
              <div
                className="chartProgressFill"
                style={{
                  width: `${pct}%`,
                  backgroundColor: style.fill,
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
